import { useEffect, useRef } from 'react';
import useTimeline from '@src/hooks/useTimeline';
import { clipRevealDelta } from '@src/services/timeline';

/**
 * Scrolls the track so a block's clip is visible. Does nothing while
 * `revealBlockId` is null, or when that clip is already fully on screen.
 *
 * The editor sets the id as a one-shot request and clears it shortly after —
 * right after appending a block from the track, or when it jumps the block list
 * to a block the user cannot see (e.g. the first one with an error). Clicking a
 * clip does NOT set it: that clip is already under the pointer, so moving the
 * track from there would only take it away from the user.
 *
 * Revealing the LAST clip reaches past it to the append button, which sits
 * beyond the clip it just created (and beyond the extraction skeleton): "Thêm
 * khối" always appends, so stopping at the clip would push the button the user
 * is clicking off screen. `appendRef` is what makes that the END OF THIS LANE
 * rather than the end of the scroll area — the scroll area is as wide as the
 * LONGEST track, so a shorter voice track would otherwise scroll far past its
 * own content. Any other clip gets the plain minimal scroll.
 *
 * @param {{ clips: {id: string, startSec: number, endSec: number}[],
 *           revealBlockId: string | null,
 *           appendRef?: {current: HTMLElement | null} }} params
 */
const useClipReveal = ({ clips, revealBlockId, appendRef }) => {
  const { secToPx, pxPerSec, viewportRef, scrollViewportBy } = useTimeline();

  // The (block, scale) pair already revealed. The effect below sees plenty of
  // re-runs that are none of its business while a request is live — typing in
  // the new block rebuilds `clips`, scrolling rebuckets `visibleRange` — and
  // re-scrolling on those would fight a user who has scrolled elsewhere.
  const revealedRef = useRef(null);

  useEffect(() => {
    if (!revealBlockId) {
      revealedRef.current = null;
      return;
    }
    // The scale is part of the key on purpose. Appending a block grows the
    // project duration, and the timeline re-fits its zoom for that one commit
    // later (a parent effect, so it lands after this one) — revealing again at
    // the settled scale corrects a position measured at the stale one, and costs
    // nothing when the clip is already in view.
    const key = `${revealBlockId}:${pxPerSec}`;
    if (revealedRef.current === key) return;

    const el = viewportRef.current;
    // A freshly appended block can be requested before redux holds it; the
    // render that adds it re-runs this effect, so no retry timer is needed.
    const clip = clips.find(({ id }) => id === revealBlockId);
    if (!el || !clip) return;

    revealedRef.current = key;
    // For the last clip, reach to the append button's right edge instead of the
    // clip's. The button is positioned inside the lane, which spans the content
    // from x=0, so its offset box is already in the same px space as secToPx.
    const isLastClip = clips[clips.length - 1].id === revealBlockId;
    const appendEl = appendRef?.current;
    const rightPx =
      isLastClip && appendEl
        ? appendEl.offsetLeft + appendEl.offsetWidth
        : secToPx(clip.endSec);
    const delta = clipRevealDelta({
      clipLeftPx: secToPx(clip.startSec),
      clipRightPx: rightPx,
      scrollLeft: el.scrollLeft,
      viewportWidth: el.clientWidth,
    });
    // scrollViewportBy ignores a zero delta, clamps to the scrollable range, and
    // notifies subscribers in the same frame so the playhead overlay doesn't lag
    // a frame behind the content.
    scrollViewportBy(delta);
  }, [
    revealBlockId,
    pxPerSec,
    clips,
    secToPx,
    viewportRef,
    scrollViewportBy,
    appendRef,
  ]);
};

export default useClipReveal;
